/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  useNavigate,
  useLocation,
  useParams,
  Navigate,
} from "react-router-dom";

import {
  Button,
  Grid as Grid, // MUI v5 Grid (leave as-is for your project)
  TextField,
  Typography,
  Container,
  Link as MuiLink,
  IconButton,
  InputAdornment,
  CardContent,
  Card,
  Box,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { useAuth } from "@/context/auth";
import { useConfirm } from "@/components/dialog/confirm";
import EnvironmentFlag from "@/components/environmentFlag";
import type { LoginRequest } from "@/services/model/loginRequest";
import { login } from "@/services/api/user-service";
import BrandLoader from "@/branding/BrandLoader";
import BrandLogo from "@/branding/BrandLogo";
import { BrandingContext } from "@/branding/BrandingProvider";

interface LoginFormValues {
  username: string;
  password: string;
  showPassword: boolean;
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation() as any;
  const { tenant } = useParams<{ tenant: string }>();
  const [loading, setLoading] = useState(false);
  const { setTokens, isAuthenticated } = useAuth();
  const confirm = useConfirm();
  const branding = useContext(BrandingContext);

  const showDialog = (title: string, message: string) =>
    confirm({ catchOnCancel: false, title, description: message });

  /** Build a safe post-login target inside the same tenant */
  const computeNext = (): string => {
    const from = location?.state?.from?.pathname as string | undefined;
    const needsAuth = Boolean(location?.state?.needsAuth);

    // Public routes to ignore as post-login targets
    const publicSegments = new Set([
      "login",
      "register",
      "forgotpassword",
      "reset-password",
    ]);

    if (tenant && from && from.startsWith(`/${tenant}/`) && needsAuth) {
      const seg = from.slice(tenant.length + 2).split("/")[0]; // after "/:tenant/"
      if (!publicSegments.has(seg)) return from; // only honor protected origins
    }

    return tenant ? `/${tenant}/dashboard` : "/";
  };

  // If already logged in, go to target
  if (isAuthenticated) {
    return <Navigate to={computeNext()} replace />;
  }

  // At least 12 chars, one lowercase, one uppercase, one digit, one non-alphanumeric
  // const passwordRegex =
  //   /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{12,}$/;

  const initialValues: LoginFormValues = {
    username: "",
    password: "",
    showPassword: false,
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Yup.object().shape({
        username: Yup.string().required("Username is required"),
        password: Yup.string().required("Password is required"),
      })}
      onSubmit={async (fields) => {
        try {
          setLoading(true);
          const req: LoginRequest = {
            username: fields.username,
            password: fields.password,
            clientId: branding?.clientId,
          };

          const response = await login(req);

          // Support both enveloped ({ data: ... }) and direct returns
          const data = (response?.data ?? response) as any;

          // Normalise fields from API (Success/Token or AccessToken)
          const success = Boolean(data?.success) || Boolean(data?.Success);
          const token =
            data?.accessToken ??
            data?.AccessToken ??
            data?.token ??
            data?.Token;

          if (success && token) {
            setTokens({
              // keep both names for compatibility across the app
              AccessToken: data?.accessToken ?? data?.AccessToken ?? undefined,
              Token: data?.token ?? data?.Token ?? undefined,
              TokenExpiresAtUnixMs:
                data?.tokenExpiresAtUnixMs ??
                data?.TokenExpiresAtUnixMs ??
                undefined,
              RefreshToken: data?.refreshToken ?? data?.RefreshToken ?? null,
              RefreshTokenExpiresAtUnixMs:
                data?.refreshTokenExpiresAtUnixMs ??
                data?.RefreshTokenExpiresAtUnixMs ??
                null,
            });
            navigate(computeNext(), { replace: true });
          } else {
            const msg = data?.message ?? data?.Message ?? "Invalid credentials";
            showDialog("Login Failed", msg);
          }
        } catch (err) {
          showDialog(
            "Login Failed",
            "An exception occurred, please contact your administrator."
          );
          if (err instanceof Error) console.error(err.message);
        } finally {
          setLoading(false);
        }
      }}
    >
      {(form) => {
        const { values, setValues, touched, errors, handleChange, handleBlur } =
          form;

        return (
          <>
            <Container maxWidth="sm" sx={{ pt: 6 }}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <EnvironmentFlag />
                      <Grid container justifyContent="center">
                        <BrandLogo size={150} shape="circle" ring shadow />
                      </Grid>

                      <Box sx={{ height: 20 }} />

                      <Form noValidate>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                          <Grid size={12}>
                            <TextField
                              type="text"
                              label="Email Address"
                              name="username"
                              value={values.username}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              required
                              variant="outlined"
                              error={!!(errors.username && touched.username)}
                              helperText={touched.username && errors.username}
                            />
                          </Grid>

                          <Grid size={12}>
                            <TextField
                              type={values.showPassword ? "text" : "password"}
                              label="Password"
                              name="password"
                              value={values.password}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              required
                              variant="outlined"
                              error={!!(errors.password && touched.password)}
                              helperText={touched.password && errors.password}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      onClick={() =>
                                        setValues({
                                          ...values,
                                          showPassword: !values.showPassword,
                                        })
                                      }
                                      edge="end"
                                      aria-label="toggle password visibility"
                                    >
                                      {values.showPassword ? (
                                        <Visibility />
                                      ) : (
                                        <VisibilityOff />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>

                          <Grid size={12}>
                            <Button
                              type="submit"
                              variant="contained"
                              color="primary"
                              fullWidth
                              size="large"
                              disabled={loading}
                              sx={{ fontWeight: 700 }}
                            >
                              LOGIN
                            </Button>
                          </Grid>
                        </Grid>
                      </Form>

                      <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                        direction="column"
                        sx={{ mt: 2 }}
                      >
                        <Typography>
                          <MuiLink
                            component="button"
                            underline="always"
                            color="secondary"
                            onClick={() =>
                              tenant
                                ? navigate(`/${tenant}/register`)
                                : navigate("/register")
                            }
                          >
                            <strong>Register an Account?</strong>
                          </MuiLink>
                        </Typography>
                        <Typography>
                          <MuiLink
                            component="button"
                            underline="always"
                            color="secondary"
                            // matches your routes: /:tenant/forgotpassword
                            onClick={() =>
                              tenant
                                ? navigate(`/${tenant}/forgotpassword`)
                                : navigate("/forgotpassword")
                            }
                          >
                            <strong>Forgot Password?</strong>
                          </MuiLink>
                        </Typography>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Container>

            <BrandLoader open={loading} />
          </>
        );
      }}
    </Formik>
  );
}
